import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { ACCOUNT_API_GET_ACCOUNT_LIST } from 'apiRoutes';
import { Table } from 'components/tables/Table';
import { accountTableColumns } from '../accountTableColumns';
import { ReopenAccountModal } from './ReopenAccountModal';
import { BlockAccountModal } from './BlockAccountModal';
import { columnFilters } from './columnFilters';
import { MontelAccount } from 'domain/account/interfaces';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { HStack } from '@montel/montelpro-shared-components-v2';

const AccountsList: FunctionComponent = () => {
	const { data, status } = useGetOnMount(`${ACCOUNT_API_GET_ACCOUNT_LIST}`);
	const [accounts, setAccounts] = useState<MontelAccount[]>([]);
	const [selectedAccount, setSelectedAccount] = useState<MontelAccount>();
	const [showReopenAccountModal, setShowReopenAccountModal] = useState(false);
	const [showBlockAccountModal, setShowBlockAccountModal] = useState(false);

	useEffect(() => {
		if (data) {
			setAccounts(data);
		}
	}, [data]);

	const onAccountBlocked = (accountId: string) => {
		updateAccountsList(accountId, true);
	};

	const onAccountReopened = (accountId: string) => {
		updateAccountsList(accountId, false);
	};

	const updateAccountsList = (
		accountId: string,
		newLockedStatus: boolean,
	) => {
		const updatedAccounts = accounts.map((account) => {
			if (account.id === accountId) {
				return {
					...account,
					locked: newLockedStatus,
				};
			}
			return account;
		});

		setAccounts(updatedAccounts);
	};

	const columns = useMemo(
		() => [
			accountTableColumns.fullName(),
			accountTableColumns.email(),
			accountTableColumns.phoneNumber(),
			accountTableColumns.registeredCompany(),
			accountTableColumns.accountCreated(),
			accountTableColumns.appAccess(),
			accountTableColumns.activeStatus(),
			accountTableColumns.blockActions(
				setSelectedAccount,
				setShowBlockAccountModal,
				setShowReopenAccountModal,
			),
		],
		[data],
	);

	return (
		<HStack fullWidth>
			<Table<MontelAccount>
				columns={columns}
				filterSettings={columnFilters}
				requestStatus={status}
				data={accounts}
				withPagination
				withSearch
				showRowCount
				linkTo={(row: MontelAccount) => `/accounts/users/${row.id}`}
				tableId="user_table_all_accounts"
			/>
			<ReopenAccountModal
				user={selectedAccount}
				onSuccess={onAccountReopened}
				setModalOpen={setShowReopenAccountModal}
				isOpen={showReopenAccountModal}
			/>
			<BlockAccountModal
				user={selectedAccount}
				onSuccess={onAccountBlocked}
				setModalOpen={setShowBlockAccountModal}
				isOpen={showBlockAccountModal}
			/>
		</HStack>
	);
};

export default AccountsList;
